import React, { useState, useEffect } from 'react';
import {
  InputAdornment,
  IconButton,
  ThemeProvider,
  Box,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { updatePassword as updatePasswordApi } from '../DataService';
import { theme } from '../styles/theme/theme';
import {
  AuthPageContainer,
  AuthFormCard,
  AuthFormContainer,
  AuthPageTitle,
  AuthSubtitle,
  AuthErrorMessage,
  AuthSuccessMessage,
  AuthTextField,
  AuthGradientButton,
} from '../styles/components';

const UpdatePassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get('id');

    if (idFromUrl) {
      setUserId(idFromUrl);
      setIsReset(true);
    } else {
      const idFromCookie = Cookies.get('UserId');
      if (idFromCookie) {
        setUserId(idFromCookie);
      } else {
        setError('Invalid session or link. Please sign in.');
      }
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (!userId) {
      setError('User ID not found. Please sign in again.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await updatePasswordApi(
        {
          OldPassword: formData.oldPassword,
          NewPassword: formData.newPassword,
        },
        userId,
        isReset
      );
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to update password.');
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthPageContainer>
        <AuthFormCard elevation={6}>
          <AuthPageTitle variant="h5">
            {isReset ? 'Reset Password' : 'Update Password'}
          </AuthPageTitle>
          
          {!success ? (
            <>
              <AuthSubtitle variant="body2">
                {isReset
                  ? 'Please enter your new password.'
                  : 'Enter your current password and choose a new one.'}
              </AuthSubtitle>

              <Box component="form" onSubmit={handleSubmit}>
                <AuthFormContainer>
                  {!isReset && (
                    <AuthTextField
                      required
                      fullWidth
                      name="oldPassword"
                      label="Current Password"
                      type={showOldPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={formData.oldPassword}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowOldPassword(!showOldPassword)}
                              edge="end"
                            >
                              {showOldPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <AuthTextField
                    required
                    fullWidth
                    name="newPassword"
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <AuthTextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <AuthErrorMessage>
                      {error}
                    </AuthErrorMessage>
                  )}

                  <AuthGradientButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </AuthGradientButton>
                </AuthFormContainer>
              </Box>
            </>
          ) : (
            <>
              <AuthSuccessMessage>
                Your password has been updated successfully.
              </AuthSuccessMessage>

              <AuthGradientButton
                fullWidth
                variant="contained"
                onClick={() => navigate('/sign-in')}
              >
                Go to Sign In
              </AuthGradientButton>
            </>
          )}
        </AuthFormCard>
      </AuthPageContainer>
    </ThemeProvider>
  );
};

export default UpdatePassword; 